import React from 'react';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import {
  Heading, Link, ListItem, OrderedList, Text, UnorderedList,
} from '@chakra-ui/react';

interface IMarkdownProps {
  markdown: string
  // Set when the copy sits under a section heading the page already renders,
  // such as an event's "About this event" card. The author's headings then
  // stay smaller than that title instead of out-sizing it.
  nested?: boolean
}

const PAGE_HEADINGS = {
  h1: { as: 'h2', size: 'lg' },
  h2: { as: 'h3', size: 'lg', mb: 3 },
  h3: { as: 'h4', size: 'md', mb: 2 },
};

// The theme only gives the lg and md heading sizes the body face; smaller
// sizes fall back to the Larken display face, which read as a different
// typeface from the card title above them.
const NESTED_HEADINGS = {
  h1: {
    as: 'h3', size: 'sm', fontFamily: 'body', mt: 4, mb: 2,
  },
  h2: {
    as: 'h3', size: 'sm', fontFamily: 'body', mt: 4, mb: 2,
  },
  h3: {
    as: 'h4', size: 'xs', fontFamily: 'body', mt: 3, mb: 2,
  },
};

const MarkdownView = ({ markdown, nested = false }: IMarkdownProps) => {
  const headings = nested ? NESTED_HEADINGS : PAGE_HEADINGS;
  const options: MarkdownToJSX.Options = {
    overrides: {
      h1: { component: Heading, props: headings.h1 },
      h2: { component: Heading, props: headings.h2 },
      h3: { component: Heading, props: headings.h3 },
      p: {
        component: Text,
        props: {
          mb: 2,
        },
      },
      // Chakra's CSS reset strips the indent and marker browsers give a bare
      // <ul>, so an author's bullet list arrived as unbroken lines of text.
      // Chakra's own list components put both back.
      ul: {
        component: UnorderedList,
        props: {
          mb: 3,
          pl: 2,
          spacing: 1,
        },
      },
      ol: {
        component: OrderedList,
        props: {
          mb: 3,
          pl: 2,
          spacing: 1,
        },
      },
      li: {
        component: ListItem,
      },
      a: {
        component: Link,
        props: {
          color: 'blue.600',
          textDecoration: 'underline',
          isExternal: true,
        },
      },
    },
  };
  return (
    <Markdown options={options}>{ markdown }</Markdown>
  );
};
export default MarkdownView;
